import { Injectable } from '@angular/core';
import { ActionSheet, ActionSheetOptions } from '@awesome-cordova-plugins/action-sheet/ngx';
import { CameraOptions, Camera, PictureSourceType } from '@awesome-cordova-plugins/camera/ngx';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { FileTransfer, FileTransferObject } from '@awesome-cordova-plugins/file-transfer/ngx';
import { FileEntry, IFile, File } from '@awesome-cordova-plugins/file/ngx';
import { Platform } from '@ionic/angular';

import { ErrorAndLoadingService } from '../error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root',
})
export class FileService {

  files: IFile[];
  filesEntry: FileEntry[];

  constructor(
    private actionSheet: ActionSheet,
    private camera: Camera,
    private platform: Platform,
    private file: File,
    private firebaseService: FirebaseService,
    private errorService: ErrorAndLoadingService,
    // tslint:disable-next-line: deprecation
    private fileTransfer: FileTransfer,
    private fileOpener: FileOpener,
  ) {
    this.files = [];
    this.filesEntry = [];
  }

  get root() {
    return this.platform.is('android') ?
      this.file.externalCacheDirectory : this.file.cacheDirectory;
  }

  get path() {
    return this.file.resolveDirectoryUrl(this.root)
      .then(directory => this.file.getDirectory(directory, 'pdf', { create: true }))
      .then(directory => directory.toURL())
      .catch((e) => {
        this.errorService.presentToastWithDuration(2000, 'top', 'Error al descargar los términos', false);
        throw e;
      });
  }

  async showUploadOptions(fileNumber: number, isProfilePicture, showDeleteOption = false) {
    const buttonLabels = isProfilePicture ? ['Tomar con la cámara', 'Importar de la galería'] : ['Cámara', 'Galería'];
    showDeleteOption && buttonLabels.push('Eliminar foto actual');
    const options: ActionSheetOptions = {
      androidTheme: 5,
      title: isProfilePicture ? 'Cambiar foto de perfil' : '¿ De dónde quieres obtener el archivo ?',
      buttonLabels: buttonLabels,
      addCancelButtonWithLabel: 'Cancelar',
    };
    if (this.platform.is('cordova')) {
      return await this.actionSheet.show(options)
      .then((buttonIndex: number) => {
        if (buttonIndex === 1) {
          return this.showCamera(fileNumber, this.camera.PictureSourceType.CAMERA);
        } else if (buttonIndex === 2) {
          return this.showCamera(fileNumber, this.camera.PictureSourceType.PHOTOLIBRARY);
        } else if (showDeleteOption && buttonIndex === 3) {
          return 'delete';
        }
        return false;
      });
    }
    return false;
  }

  showCamera(fileNumber: number, sourceType: PictureSourceType) {
    const options: CameraOptions = {
      quality: 50,
      sourceType: sourceType,
      destinationType: this.camera.DestinationType.FILE_URI,
      encodingType: this.camera.EncodingType.JPEG,
      mediaType: this.camera.MediaType.PICTURE,
      saveToPhotoAlbum: false,
      correctOrientation: true,
    };
    return this.camera.getPicture(options)
    .then((filePath) => {
      return this.resolveFileURL(fileNumber, filePath);
    }, (error) => {
      if (error === 'No Image Selected') {
        return null;
      }
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al procesar el archivo', false);
      return null;
    });
  }

  resolveFileURL(fileNumber: number, filePath: any) {
    return this.file.resolveLocalFilesystemUrl(filePath).then((fileEntry: FileEntry) => {
      return this.getFile(fileEntry, fileNumber);
    });
  }

  getFile(fileEntry: FileEntry, fileNumber: number) {
    return new Promise((resolve) => {
      fileEntry.file((fileObj) => {
        this.files[fileNumber] = fileObj;
        this.filesEntry[fileNumber] = fileEntry;
        const first = this.checkFileData(fileNumber);
        resolve(first);
      });
    });
  }

  checkFileData(fileNumber: number) {
    if (this.files[fileNumber].size > 8192000) {
      this.errorService.presentToastWithDuration(2000, 'top', 'El archivo es muy grande', false);
      this.files = this.files.splice(fileNumber, 1);
      return null;
    } else if (['image/gif', 'image/jpeg', 'application/pdf'].indexOf(this.files[fileNumber].type) === -1) {
      this.errorService.presentToastWithDuration(2000, 'top', 'El archivo es inválido', false);
      this.files = this.files.splice(fileNumber, 1);
      return null;
    }
    return true;
  }

  async makeFileIntoBlob(fileNumber: number): Promise<Blob> {
    const path = this.filesEntry[fileNumber].nativeURL.substring(0, this.filesEntry[fileNumber].nativeURL.lastIndexOf('/'));
    const buffer = await this.file.readAsArrayBuffer(path, this.files[fileNumber].name);
    const imgBlob = new Blob([buffer], {
      type: 'image/jpeg',
    });
    return imgBlob;
  }

  async uploadAllFiles(firebasePath: string, fileNames: string[]) {
    const snapshots: firebase.default.storage.UploadTask[] = [];
    try {
      for (let index = 0; index < this.files.length; index++) {
        const imgBlob = await this.makeFileIntoBlob(index);
        snapshots.push(this.firebaseService.uploadFileFromStorage(imgBlob, fileNames[index], firebasePath));
      }
      return snapshots;
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al subir los archivos', false);
      throw err;
    }
  }

  async showPDF(blob: Blob, name: string = '') {
    try {
      const safeName = name.replace(/[/\\?%*:|"<>]/g, '-');
      const fileName = `racional_${safeName}_${+new Date()}.pdf`;
      const path = await this.path;
      await this.file.writeFile(path, fileName, blob, { replace: true });
      const filePath = path + fileName;
      return this.fileOpener.open(filePath, 'application/pdf');
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al abrir los términos', false);
      throw err;
    }

  }

  async downloadAndOpenFile(url, type) {
    const transfer: FileTransferObject = this.fileTransfer.create();
    transfer.download(url, `${this.file.dataDirectory}/${type}.pdf`)
    .then((entry) => {
      this.fileOpener.open(entry.toURL(), 'application/pdf');
    });
    this.firebaseService.logEvent(`${type}_review`, {});
  }

  uploadFromInput(file: Blob, firebasePath: string, fileName: string) {
    if (file.size > 8192000) {
      this.errorService.presentToastWithDuration(2000, 'top', 'El archivo es muy grande', false);
      return null;
    } else if (['image/gif', 'image/jpeg', 'image/png', 'application/pdf'].indexOf(file.type) === -1) {
      this.errorService.presentToastWithDuration(2000, 'top', 'El archivo es inválido', false);
      return null;
    }
    try {
      return this.firebaseService.uploadFileFromStorage(file, fileName, firebasePath);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al subir los archivos', false);
      throw err;
    }
  }

  // async downloadFile(needURL: boolean, name: string, path: string) {
  //     const url = await this.firebaseService.getFileURLFromStorage(name, path);
  //     if (needURL) {
  //       return url;
  //     }
  //     return this.firebaseService.downloadFileFromStorage(url);
  // }

}
